import { heroContent } from '@/mocks/homeData';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function HeroSection() {
  const { ref, isVisible } = useScrollReveal(0.05);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[750px] md:min-h-[800px] flex items-center overflow-hidden"
    >
      <img
        src={heroContent.backgroundImage}
        alt="전문 법률사무소 배경"
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="absolute inset-0 bg-foreground-950/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/65 via-foreground-950/45 to-foreground-950/25" />
      <div className="absolute inset-0 backdrop-blur-[2px]" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 xl:px-16 py-24 md:py-28">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16 max-w-7xl mx-auto">
          <div className="max-w-xl w-full">
            <div
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <span className="inline-block px-4 py-1.5 rounded-full text-xs md:text-sm font-medium tracking-wider bg-primary-500/20 text-primary-300 border border-primary-500/30 mb-6">
                {heroContent.mainKeyword}
              </span>
            </div>

            <h1
              className={`transition-all duration-700 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ fontFamily: "'Gothic A1', sans-serif" }}
            >
              <span className="block text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight tracking-tight">
                {heroContent.attorneyName}
              </span>
              <span className="block mt-2 text-lg md:text-2xl lg:text-3xl font-medium text-primary-400 leading-relaxed" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                용인·수원이혼전문변호사
              </span>
            </h1>

            <p
              className={`mt-6 md:mt-8 text-base md:text-lg text-background-200 leading-relaxed max-w-lg transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {heroContent.subtitle}
            </p>

            <div
              className={`mt-6 md:mt-8 flex flex-wrap gap-3 transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {heroContent.badges.map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs md:text-sm font-medium bg-background-800/70 text-background-200 border border-background-700/60 backdrop-blur-sm"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-primary-400" />
                  </span>
                  {badge}
                </span>
              ))}
            </div>

            <div
              className={`mt-8 md:mt-10 flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <a
                href={`tel:${heroContent.ctaPhone}`}
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-base font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-lg shadow-primary-500/25"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-lg" />
                </span>
                {heroContent.ctaPhone}
              </a>
              <a
                href="https://m.booking.naver.com/booking/6/bizes/1539443/items/7203561?area=pll&lang=ko&startDate=2026-08-10&theme=place"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-medium border border-background-500/60 text-background-200 hover:bg-background-800/40 hover:border-background-400 transition-all duration-300 whitespace-nowrap backdrop-blur-sm"
              >
                상담 예약하기
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line" />
                </span>
              </a>
            </div>

            <div
              className={`mt-4 lg:hidden transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <a
                href="https://blog.naver.com/sjj09211"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-background-50 hover:brightness-110 transition-all duration-300 whitespace-nowrap w-full sm:w-auto"
                style={{ backgroundColor: 'rgba(3, 199, 90, 0.9)' }}
              >
                <span className="w-5 h-5 flex items-center justify-center rounded-sm bg-white text-[#03C75A] font-black text-xs leading-none">N</span>
                블로그 바로가기
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </a>
            </div>
          </div>

          <div
            className={`hidden lg:flex flex-col items-center relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
            }`}
          >
            <div className="flex items-end gap-4">
              {/* 네이버 블로그 카드 — 사진 카드 왼쪽에 완전히 분리, 나란히 */}
              <a
                href="https://blog.naver.com/sjj09211"
                target="_blank"
                rel="noopener noreferrer"
                className="w-[84px] h-[84px] xl:w-[100px] xl:h-[100px] rounded-full flex flex-col items-center justify-center gap-2 relative shadow-xl shadow-black/20 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 z-20 group shrink-0"
                style={{ backgroundColor: 'rgba(3, 199, 90, 0.8)' }}
              >
                <span className="text-white font-black text-4xl leading-none transition-all duration-300 group-hover:opacity-0 group-hover:scale-0">N</span>
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/30 transition-all duration-300 group-hover:opacity-0 group-hover:scale-0">
                  <i className="ri-arrow-right-line text-[10px] text-white" />
                </span>
                <span className="absolute flex flex-col items-center text-white font-bold text-xs text-center leading-tight opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 px-2">
                  <span>장수진 블로그</span>
                  <span>바로가기</span>
                </span>
              </a>

              <div className="relative">
                <div className="w-[380px] xl:w-[420px] aspect-[3/4] rounded-2xl overflow-hidden bg-background-200">
                  <img
                    src={heroContent.attorneyPhotoDark}
                    alt={heroContent.attorneyName}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* 등록증서 플로팅 카드 - 오른쪽 하단 */}
                <div className="absolute -right-6 bottom-2 w-[120px] xl:w-[140px] bg-background-50 rounded-xl border border-background-200 shadow-xl shadow-black/25 overflow-hidden hover:-translate-y-1 transition-transform duration-300">
                  <div className="aspect-[3/4] w-full">
                    <img
                      src={heroContent.certificateImage}
                      alt="대한변호사협회 전문분야 등록증서"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="px-3 py-2 bg-background-50 border-t border-background-200">
                    <p className="text-[10px] xl:text-xs text-foreground-600 font-medium text-center leading-tight">
                      대한변협등록증서
이혼전문변호사
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}